import { Col, Collapse, Form as AntForm, Row } from "antd";
import type { FormInstance } from "antd/es/form/Form";
import { useForm } from "antd/es/form/Form";
import {
    Children,
    type FC,
    isValidElement,
    type KeyboardEvent,
    type PropsWithChildren,
    type ReactNode,
    useEffect,
    useMemo,
    useState,
} from "react";
import { useTranslation } from "react-i18next";
import { useLocation } from "react-router";

import { ActionBar } from "../ActionBar/ActionBar";
import { ResetButton } from "../FeatureButtons/ResetButton";
import { SearchButton } from "../FeatureButtons/SearchButton";
import { VerticalFormLabelColonStyle } from "../Form/formFieldLabelPositioning/styles/FormFieldsStyle";
import { withLabelColon } from "../Form/formFieldLabelPositioning/withLabelColon";
import { loadPersistedQueryConfig } from "../Table";
import { TagsContainer, TagStyle } from "../Tag/TagStyle";
import { useFormatFilterValue } from "./filterValueFormatter";

/**
 * Extracts field name → label mappings from Form.Item children.
 */
const extractFieldLabels = (children: ReactNode): Record<string, string> => {
    const labels: Record<string, string> = {};
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const props = child.props as { name?: string; label?: ReactNode; children?: ReactNode };
            if (props.name && typeof props.label === "string") {
                labels[props.name] = props.label;
            }
            if (props.children) {
                Object.assign(labels, extractFieldLabels(props.children));
            }
        }
    });
    return labels;
};

/**
 * Finds the first `fieldConfig.urlToFetchOptions` (e.g. of a `SelectDynamic`) within the given children.
 */
const findDatasourceUrl = (children: ReactNode): string | undefined => {
    let url: string | undefined;
    Children.forEach(children, (child) => {
        if (url || !isValidElement(child)) return;
        const props = child.props as { fieldConfig?: { urlToFetchOptions?: string }; children?: ReactNode };
        url = props.fieldConfig?.urlToFetchOptions ?? findDatasourceUrl(props.children);
    });
    return url;
};

/**
 * Extracts field name → options datasource URL mappings from Form.Item children, so filter tags can
 * resolve a selected value to its display name from the right datasource.
 */
const extractFieldDatasources = (children: ReactNode): Record<string, string> => {
    const datasources: Record<string, string> = {};
    Children.forEach(children, (child) => {
        if (isValidElement(child)) {
            const props = child.props as { name?: string; children?: ReactNode };
            const url = props.name ? findDatasourceUrl(props.children) : undefined;
            if (props.name && url) {
                datasources[props.name] = url;
            } else if (props.children) {
                Object.assign(datasources, extractFieldDatasources(props.children));
            }
        }
    });
    return datasources;
};

/**
 * Search Form properties
 * */
type SearchFormProps = {
    searchCallback?: Function;
    onReset?: () => void;
    form?: FormInstance;
} & PropsWithChildren;

/**
 * Default component for the search form for the resource pages
 * */
export const SearchForm: FC<SearchFormProps> = ({ searchCallback, onReset, form: externalForm, children }) => {
    const [internalForm] = useForm();
    const form = externalForm || internalForm;
    const { Panel } = Collapse;
    const { t } = useTranslation();
    const { pathname } = useLocation();
    const [activeFilters, setActiveFilters] = useState<Record<string, unknown>>({});
    const formatFilterValue = useFormatFilterValue();
    const fieldLabels = useMemo(() => extractFieldLabels(children), [children]);
    const fieldDatasources = useMemo(() => extractFieldDatasources(children), [children]);

    // Restores filters persisted (by ResourceManager, keyed on this page's URL path) from a previous
    // visit to this page, so the form fields and filter tags reflect them on mount.
    useEffect(() => {
        const persistedFilters = loadPersistedQueryConfig<Record<string, unknown>>(pathname)?.filters;
        if (persistedFilters && Object.keys(persistedFilters).length > 0) {
            form.setFieldsValue(persistedFilters);
            setActiveFilters(
                Object.fromEntries(
                    Object.entries(persistedFilters).filter(
                        ([k, v]) => k in fieldLabels && v !== undefined && v !== null && v !== "" && v !== false,
                    ),
                ),
            );
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const onResetClicked = () => {
        form.resetFields();
        setActiveFilters({});
        if (onReset) {
            onReset();
        } else if (searchCallback) {
            searchCallback({});
        }
    };

    const onSearchClicked = () => {
        if (searchCallback) {
            const values = form.getFieldsValue();
            searchCallback(values);
            if (fieldLabels) {
                setActiveFilters(
                    Object.fromEntries(
                        Object.entries(values).filter(
                            ([k, v]) => k in fieldLabels && v !== undefined && v !== null && v !== "" && v !== false,
                        ),
                    ),
                );
            }
        }
    };

    // antd Select handles Enter itself (opening the dropdown), so it never reaches the form. When a select
    // with a closed dropdown has focus, submit the search instead; an open dropdown still selects the option.
    const onKeyDownCapture = (e: KeyboardEvent<HTMLFormElement>) => {
        if (e.key !== "Enter") {
            return;
        }
        const target = e.target as HTMLElement;
        if (target.closest(".ant-select") && target.getAttribute("aria-expanded") !== "true") {
            e.preventDefault();
            e.stopPropagation();
            form.submit();
        }
    };

    const removeFilter = (key: string) => {
        form.setFieldValue(key, undefined);
        const updated = { ...activeFilters };
        delete updated[key];
        setActiveFilters(updated);
        if (searchCallback) {
            searchCallback(form.getFieldsValue());
        }
    };

    const wrapInCols = (nodes: ReactNode) =>
        Array.isArray(nodes) ? (
            nodes.map((child, i) => (
                <Col key={i} xs={24} sm={12} md={12} lg={6} xl={6}>
                    {child}
                </Col>
            ))
        ) : (
            <Col xs={24} sm={12} md={12} lg={6} xl={6}>
                {nodes}
            </Col>
        );

    const activeFilterEntries = fieldLabels ? Object.entries(activeFilters) : [];

    return (
        <>
            <Collapse>
                <Panel header={t("button.search")} key="search">
                    {/* Search button is a submit button inside the form, so pressing Enter in a field triggers the search */}
                    <VerticalFormLabelColonStyle>
                        <AntForm
                            form={form}
                            name="search"
                            layout="vertical"
                            autoComplete="off"
                            onFinish={onSearchClicked}
                            onKeyDownCapture={onKeyDownCapture}
                        >
                            <Row gutter={[24, 1]}>{wrapInCols(children)}</Row>
                            <ActionBar className="left">
                                <SearchButton isDisabled={false} htmlType="submit" />
                                <ResetButton isDisabled={false} callback={onResetClicked} />
                            </ActionBar>
                        </AntForm>
                    </VerticalFormLabelColonStyle>
                </Panel>
            </Collapse>
            {activeFilterEntries.length > 0 && (
                <TagsContainer $marginTop="16px">
                    {activeFilterEntries.map(([key, value]) => (
                        <TagStyle key={key} closable onClose={() => removeFilter(key)}>
                            {withLabelColon(fieldLabels![key] ?? key)} {formatFilterValue(value, fieldDatasources[key])}
                        </TagStyle>
                    ))}
                </TagsContainer>
            )}
        </>
    );
};
